import * as React from 'react'

import {
  CardList,
  ICardListHandle,
} from '~/client/src/shared/components/CardList'
import CardListEmpty from '~/client/src/shared/components/CardList/CardListEmpty'
import { encodeDrillDownFilterSelectionId } from '~/client/src/shared/components/DrillDownFilter/filterSelectionIds'
import { useCardListCore } from '~/client/src/shared/hooks/useCardListCore/useCardListCore'
import { useDebouncedValue } from '~/client/src/shared/hooks/useDebouncedValue'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

import {
  applyMapViewsTransform,
  FilterSelection,
  FILTER_FACET_SCOPE,
  GroupKey,
  IScopeOption,
  ISortState,
  SortDirection,
  SortKey,
} from './applyMapViewsTransform'
import {
  buildMapViewsGroupConfig,
  IMapViewsHeaderMeta,
} from './buildMapViewsGroupConfig'
import { compareNaturalLabels } from './compareNaturalLabels'
import type { IMapViewsSidebarView } from './MapViewsSidebar'
import { ItemsCounterLine } from './MapViewsSidebarCounterLine'
import { MapViewsSidebarFiltersBar } from './MapViewsSidebarFiltersBar'
import {
  IGroupHeaderRow,
  LBS_LEVEL_INDENT_PX,
  MapViewsGroupHeader,
} from './MapViewsSidebarGroupHeader'
import { MapViewsSidebarHeader } from './MapViewsSidebarHeader'
import {
  IMapViewItem,
  IMapViewRow,
  MapViewsSidebarListItem,
} from './MapViewsSidebarListItem'
import { PublishToAppKey } from './PublishToChipRow'

const ESTIMATED_ITEM_HEIGHT = 140
const ESTIMATED_HEADER_HEIGHT = 32
const SEARCH_DEBOUNCE_MS = 300
const MAP_VIEW_ITEM_FOCUS_SELECTOR = '[data-slot="map-views-sidebar-list-item"]'

function buildHeaderRow(
  bucketKey: string,
  label: string,
  count: number,
  depth: number,
  meta: IMapViewsHeaderMeta,
): IGroupHeaderRow {
  const base = {
    key: `header:${bucketKey}`,
    bucketKey,
    label,
    count,
  }
  switch (meta.headerKind) {
    case 'lbs':
      return {
        ...base,
        headerKind: 'lbs',
        level: depth,
        scopeIconName: meta.scopeIconName,
        scopeColor: meta.scopeColor,
      }
    case 'scope':
      return {
        ...base,
        headerKind: 'scope',
        scopeIconName: meta.scopeIconName,
        scopeColor: meta.scopeColor,
      }
    case 'type':
      return {
        ...base,
        headerKind: 'type',
        capability: meta.capability,
      }
  }
}

interface IProps {
  title?: string
  items: IMapViewItem[]
  selectedId?: string
  onSelect: (id: string) => void
  onClose: () => void
  className?: string
  /** Singleton — only the active card's preview ever mounts (see `useActiveLiveMapSlot`). */
  renderLivePreview?: (item: IMapViewRow) => React.ReactNode
  /** Menu needs onRename+onDelete; drag handle needs onReorder. */
  canEdit?: boolean
  onRename?: (id: string) => void
  onDelete?: (id: string) => void
  onCopy?: (id: string) => void
  onSetLocation?: (id: string, anchor: HTMLElement) => void
  onReorder?: (
    fromId: string,
    toId: string,
    position: 'before' | 'after',
  ) => void
  footerSlot?: React.ReactNode
  availableApps?: PublishToAppKey[]
  onOpenPublishTo?: (id: string, anchor: HTMLElement) => void
  initialView?: IMapViewsSidebarView
  onViewChange?: (view: IMapViewsSidebarView) => void
  /** True when wrapped in a surface that sizes to content (max-h-* with no h-* pin). Drops CardList's `size` containment. */
  parentSizesToContent?: boolean
}

export const MapViewsSidebarContent: React.FC<IProps> = ({
  title = Localization.translator.selectMapView,
  items,
  selectedId,
  onSelect,
  onClose,
  className,
  renderLivePreview,
  canEdit,
  onRename,
  onDelete,
  onCopy,
  onSetLocation,
  onReorder,
  footerSlot,
  availableApps,
  onOpenPublishTo,
  initialView,
  onViewChange,
  parentSizesToContent = false,
}) => {
  const cardListRef = React.useRef<ICardListHandle>(null)

  const [searchTerm, setSearchTerm] = React.useState(
    initialView?.searchTerm ?? '',
  )
  const [sort, setSort] = React.useState<ISortState>(
    initialView?.sort ?? { key: 'default', direction: 'asc' },
  )
  const [group, setGroup] = React.useState<GroupKey>(
    initialView?.group ?? 'none',
  )
  const [filterSelection, setFilterSelection] = React.useState<FilterSelection>(
    initialView?.filterSelection ?? [],
  )
  // Controlled so all-bands toggle can operate on every bucket key (not just visible ones).
  const [collapsedKeys, setCollapsedKeys] = React.useState<Set<string>>(
    () => new Set(),
  )

  // Search is exempt — drop targets resolve by stable id, so dragging inside a
  // filtered view still moves the right entry.
  const isReorderable =
    sort.key === 'default' && group === 'none' && filterSelection.length === 0

  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS)

  // Skip initial-mount emission so `initialView` consumers don't see an echo.
  // useLayoutEffect mirror keeps ref writes out of render (concurrent-safe).
  const onViewChangeRef = React.useRef(onViewChange)
  React.useLayoutEffect(() => {
    onViewChangeRef.current = onViewChange
  })
  const initialMountRef = React.useRef(true)
  React.useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false
      return
    }
    onViewChangeRef.current?.({ searchTerm, sort, group, filterSelection })
  }, [searchTerm, sort, group, filterSelection])

  const {
    items: filteredItems,
    visibleCount,
    totalCount,
  } = React.useMemo(
    () =>
      applyMapViewsTransform(
        items,
        { sort, group, filterSelection },
        debouncedSearch,
      ),
    [items, sort, group, filterSelection, debouncedSearch],
  )

  const { groupConfig, headerMetaByKey } = React.useMemo(
    () => buildMapViewsGroupConfig(filteredItems, group),
    [filteredItems, group],
  )

  const listState = useCardListCore<IMapViewItem>({
    items: filteredItems,
    groupConfig,
    collapsedKeys,
    onCollapsedKeysChange: setCollapsedKeys,
  })

  // Shares `compareNaturalLabels` with the body's 'location' sort so chip options
  // match band order.
  const scopeOptions = React.useMemo<IScopeOption[]>(() => {
    const labels = new Set<string>()
    for (const it of items) labels.add(it.scopeLabel)
    return [...labels].sort(compareNaturalLabels).map(label => ({
      id: encodeDrillDownFilterSelectionId(FILTER_FACET_SCOPE, label),
      label,
    }))
  }, [items])

  // Date sorts default to desc ("newest first"); re-clicking same key keeps
  // user-toggled direction.
  const handleSortKeyChange = React.useCallback((key: SortKey): void => {
    setSort(prev => {
      if (prev.key === key) return prev
      return {
        key,
        direction:
          key === 'lastUpdated' || key === 'createdAt' ? 'desc' : 'asc',
      }
    })
  }, [])
  const handleSortDirectionChange = React.useCallback(
    (direction: SortDirection): void => {
      setSort(prev => ({ ...prev, direction }))
    },
    [],
  )
  // Walks `listState.flatItems` (render order, skips collapsed) and delegates to CardList's
  // imperative focusItemId.
  const handleListKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      const visibleItems = listState.flatItems
      if (visibleItems.length === 0) return
      const active = document.activeElement as HTMLElement | null
      const focusedWrapper = active?.closest<HTMLElement>('[data-item-id]')
      const currentId = focusedWrapper?.getAttribute('data-item-id') ?? null
      const currentIndex = currentId
        ? visibleItems.findIndex(it => it.id === currentId)
        : -1
      let nextIndex: number | null = null
      if (e.key === 'ArrowDown') {
        nextIndex =
          currentIndex < 0
            ? 0
            : Math.min(currentIndex + 1, visibleItems.length - 1)
      } else if (e.key === 'ArrowUp') {
        nextIndex =
          currentIndex < 0
            ? visibleItems.length - 1
            : Math.max(currentIndex - 1, 0)
      } else if (e.key === 'Home') {
        nextIndex = 0
      } else if (e.key === 'End') {
        nextIndex = visibleItems.length - 1
      } else {
        return
      }
      e.preventDefault()
      if (nextIndex === null || nextIndex === currentIndex) return
      cardListRef.current?.focusItemId(
        visibleItems[nextIndex].id,
        MAP_VIEW_ITEM_FOCUS_SELECTOR,
      )
    },
    [listState.flatItems],
  )

  return (
    <div
      className={cn(
        'tw:flex tw:h-full tw:w-full tw:min-h-0 tw:flex-col tw:bg-popover',
        className,
      )}
    >
      <MapViewsSidebarHeader title={title} onClose={onClose} />
      <MapViewsSidebarFiltersBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sort={sort}
        onSortKeyChange={handleSortKeyChange}
        onSortDirectionChange={handleSortDirectionChange}
        group={group}
        onGroupChange={setGroup}
        filterSelection={filterSelection}
        onFilterApply={setFilterSelection}
        onFilterReset={() => setFilterSelection([])}
        scopeOptions={scopeOptions}
      />
      <ItemsCounterLine
        visible={visibleCount}
        total={totalCount}
        listState={listState}
      />
      <CardList<IMapViewItem>
        ref={cardListRef}
        items={filteredItems}
        listState={listState}
        role="listbox"
        ariaLabel={title}
        onKeyDown={handleListKeyDown}
        estimatedItemHeight={ESTIMATED_ITEM_HEIGHT}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        parentSizesToContent={parentSizesToContent}
        className="tw:flex-1 tw:min-h-0"
        empty={
          <CardListEmpty
            message={
              searchTerm
                ? Localization.translator.noMapViewsMatchingX(searchTerm)
                : Localization.translator.noMapViewsConfigured
            }
          />
        }
        renderGroupHeader={(group, onToggle) => {
          const meta = headerMetaByKey.get(group.key)
          if (!meta) return null
          return (
            <MapViewsGroupHeader
              header={buildHeaderRow(
                group.key,
                group.label,
                group.count,
                group.depth,
                meta,
              )}
              collapsed={listState.collapsedKeys.has(group.key)}
              onToggle={onToggle}
              lbsLineLevel={group.depth}
            />
          )
        }}
        renderItem={(item, depth) => (
          <MapViewsSidebarListItem
            item={item}
            isSelected={item.id === selectedId}
            onSelect={onSelect}
            renderLivePreview={renderLivePreview}
            canEdit={canEdit}
            onRename={onRename}
            onDelete={onDelete}
            onCopy={onCopy}
            onSetLocation={onSetLocation}
            onReorder={isReorderable ? onReorder : undefined}
            availableApps={availableApps}
            onOpenPublishTo={onOpenPublishTo}
            contentIndentPx={(depth ?? 0) * LBS_LEVEL_INDENT_PX}
            lbsLineLevel={depth ?? 0}
          />
        )}
      />
      {footerSlot && (
        // `-mt-px` overlaps footer border-t with last item's border-b so they read as a single line.
        // `relative z-10` paints the footer above virtualizer-translated cards (their own stacking contexts).
        <div className="tw:relative tw:z-10 tw:-mt-px tw:flex tw:w-full tw:shrink-0 tw:items-center tw:gap-2 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-popover tw:p-3">
          {footerSlot}
        </div>
      )}
    </div>
  )
}
